import { isEqual } from 'lodash';
import qs from 'qs';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAdvancedSearchParams } from '../utilities';

const stringify = (obj, arrayFormat) => qs.stringify(obj, { skipNulls: false, arrayFormat });
const parse = search => (search ? qs.parse(decodeURIComponent(search), { ignoreQueryPrefix: true }) : null);

const INVALID_LENGTH = 4000;
/**
 * Generic hook that is able to decode search (query) parameters component on-mount...
 * and encode state (if changed during component lifecycle) into search (query) parameters.
 * This hook can be used for any kind of state: filters, sort, view-preferences (side menu, blade opened/closed)
 *
 * @param {object} state - the init state to be encoded (if search is not provided), and to be encoded to search if changed after init.
 * @param {function} setState - the function that sets the state into the component relevant state/atom/storage/redux. <br />
 * The setState can receive the object and spread it into setFilters, setSort, setBladeOpened
 * @param {function} mapState - maps the parsed search parameters object to target object before being passed to setState.
 * @param {string} [arrayFormat] - `qs` array encoding for WRITES. Defaults to `qs`'s own `indices`
 * (`types[0]=a&types[1]=b`). Pass `'repeat'` for `types=a&types=b` when the query string has to match
 * an API that rejects the bracket form. Reads accept either, so this is safe to change per-caller.
 * */
export const useQueryStringToState = ({ state, setState, mapState = null, arrayFormat = undefined }) => {
  // using this advanced method, will maintain state if needed (for example: state: PIVOT will be maintained)
  const [, setSearchParams] = useAdvancedSearchParams();
  const { search } = useLocation();
  const stateRef = useRef({ search, changedBySearch: false, init: true });

  useEffect(() => {
    const newSearch = (search[0] === '?' ? search.substring(1) : search)?.trim();
    if (!newSearch || newSearch.length > INVALID_LENGTH) return;
    const newState = parse(newSearch);
    const adaptedState = mapState?.(newState) || newState;
    if (!isEqual(adaptedState, state)) {
      stateRef.current = { search: newSearch, changedBySearch: true, init: false };
      setState(adaptedState);
    } else {
      stateRef.current = { search: newSearch, changedBySearch: false, init: false };
    }
    /** Handled when the screen is loaded or when url params changed. */
  }, [search]);

  useEffect(() => {
    if (stateRef.current.changedBySearch) {
      stateRef.current = { ...stateRef.current, changedBySearch: false, init: false };
    } else {
      const adaptedState = mapState?.(state) || state;
      const newSearch = stringify(adaptedState, arrayFormat);
      if (newSearch !== stateRef.current.search) {
        // replace is only applied on init (after that it adds up to the history
        // setSearchParams(newSearch, { replace: stateRef.current.init });
        setSearchParams(newSearch, { replace: true });
      }
      stateRef.current = { search: newSearch, changedBySearch: false, init: false };
    }
  }, [state]);
};
